// Arqum 2026-10-16.
(() => {
'use strict';
const CONTEXT_ATTRIBUTE = '__ips_meta_confirmed_context_v1';
const CONTEXT_SHOP = 'wdpa1m-fi.myshopify.com';
const CONTEXT_TTL_MS = 30 * 60 * 1000;
const CONTEXT_WRITE_LOCK = `ips-ad-summary-write:${CONTEXT_SHOP}:v1`;
const MAX_CONTEXT_BYTES = 3072;

function allowedHost(env) {
  return env.location?.protocol === 'https:' && ['ipshealth.co.za', 'www.ipshealth.co.za'].includes(env.location?.hostname) && env.location?.origin === `https://${env.location.hostname}`;
}

function readStorefrontPermission(env) {
  try {
    const api = env.Shopify.customerPrivacy;
    const marketingAllowed = api.marketingAllowed() === true;
    const saleOfDataAllowed = api.saleOfDataAllowed() === true;
    const choice = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : {};
    return { marketingAllowed: marketingAllowed && choice?.marketing !== 'no', saleOfDataAllowed: saleOfDataAllowed && choice?.sale_of_data !== 'no' };
  } catch { return { marketingAllowed: false, saleOfDataAllowed: false }; }
}

function permitted(value) {
  return value?.marketingAllowed === true && value?.saleOfDataAllowed === true;
}

function readGenuineMatchingCookies(text) {
  if (typeof text !== 'string' || text.length > 32768) return {};
  const matching = {};
  for (const name of ['fbp', 'fbc']) {
    const values = text.split(';').map(part => part.trim()).filter(part => part.startsWith(`_${name}=`)).map(part => part.slice(name.length + 2));
    if (!values.length || new Set(values).size !== 1) continue;
    const pattern = name === 'fbp' ? /^fb\.[12]\.\d{10,16}\.\d{1,64}$/ : /^fb\.[12]\.\d{10,16}\.[A-Za-z0-9_-]{1,500}$/;
    if (pattern.test(values[0])) matching[name] = values[0];
  }
  return matching;
}

function buildStorefrontContext({ now, sourceOrigin, userAgent, matching, privacy, cartBinding }) {
  if (!Number.isSafeInteger(now) || !/^https:\/\/(?:www\.)?ipshealth\.co\.za$/.test(sourceOrigin ?? '') || !permitted(privacy) || !/^[a-f0-9]{64}$/.test(cartBinding ?? '')) return null;
  if (typeof userAgent !== 'string' || userAgent.length < 8 || userAgent.length > 512 || /[\u0000-\u001f\u007f]/.test(userAgent)) return null;
  if (!matching || !Object.keys(matching).length || Object.keys(matching).some(key => !['fbp', 'fbc'].includes(key))) return null;
  const checkedCookies = readGenuineMatchingCookies(Object.entries(matching).map(([key, value]) => `_${key}=${value}`).join('; '));
  if (JSON.stringify(checkedCookies) !== JSON.stringify(matching)) return null;
  const observedAt = new Date(now).toISOString();
  const result = { version: 1, shop: CONTEXT_SHOP, observationBasis: 'storefront_observation', observedAt,
    expiresAt: new Date(now + CONTEXT_TTL_MS).toISOString(), sourceUrl: `${sourceOrigin}/`, sourceUrlBasis: 'observed_origin_core_projection',
    userAgent, matching: { ...matching }, privacy: { marketingAllowed: true, saleOfDataAllowed: true, observedAt, source: 'shopify_customer_privacy_api' }, cartBinding };
  return new TextEncoder().encode(JSON.stringify(result)).byteLength <= MAX_CONTEXT_BYTES ? result : null;
}

function createStorefrontContextRuntime(env, configuration = {}, now = () => Date.now()) {
  const config = Object.freeze({ ...configuration });
  const enabled = config.enabled === true && config.policyReviewed === true && config.approvedStorefrontCapture === true && allowedHost(env);
  if (!enabled) return { status: 'disabled', start: async () => ({ status: 'disabled' }), refresh: async () => ({ status: 'disabled' }), dispose() {} };
  if (typeof env.fetch !== 'function' || typeof env.navigator?.locks?.request !== 'function' || typeof env.crypto?.subtle?.digest !== 'function' || typeof env.AbortController !== 'function') return { status: 'unavailable', start: async () => ({ status: 'held' }), refresh: async () => ({ status: 'held' }), dispose() {} };
  let disposed = false;
  let started;
  let serial = Promise.resolve();
  let revision = 0;
  let eventDenied = false;
  let lastSignature = null;
  let lastWrittenAt = 0;
  let unsubscribe;
  let interval;
  const listeners = [];

  function currentPermission() {
    const value = readStorefrontPermission(env);
    return eventDenied ? { marketingAllowed: false, saleOfDataAllowed: false } : value;
  }

  function endpoint(path) {
    const root = env.Shopify?.routes?.root;
    if (!allowedHost(env) || typeof root !== 'string' || !/^\/(?:[A-Za-z0-9_-]+\/)*$/.test(root) || !['cart.js', 'cart/update.js'].includes(path)) throw new Error('cart_endpoint_unavailable');
    return new URL(root + path, env.location.origin).href;
  }

  async function request(path, options = {}, json = false) {
    const abort = new env.AbortController();
    let timer;
    const timeout = new Promise((_, reject) => { timer = env.setTimeout(() => { abort.abort(); reject(new Error('cart_timeout')); }, 3000); });
    try {
      return await Promise.race([timeout, (async () => {
        const response = await env.fetch(endpoint(path), { credentials: 'same-origin', mode: 'same-origin', redirect: 'error', referrerPolicy: 'same-origin', ...options, signal: abort.signal });
        if (!response.ok) throw new Error('cart_unavailable');
        return json ? response.json() : undefined;
      })()]);
    } finally { env.clearTimeout(timer); }
  }

  async function cart() {
    const value = await request('cart.js', {}, true);
    if (!Number.isInteger(value?.item_count) || value.item_count < 0 || typeof value.token !== 'string' || !value.token.length || value.token.length > 2048) throw new Error('cart_identity_unavailable');
    const digest = await env.crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${CONTEXT_SHOP}:${value.token}`));
    return { itemCount: value.item_count, binding: Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('') };
  }

  async function writeOwn(value) {
    await request('cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { [CONTEXT_ATTRIBUTE]: value } }) });
  }

  async function clearOwn() {
    await writeOwn('');
    lastSignature = null;
    lastWrittenAt = 0;
    return { status: 'cleared' };
  }

  async function work() {
    if (disposed) return { status: 'disposed' };
    const generation = revision;
    if (!permitted(currentPermission())) return clearOwn();
    const before = await cart();
    if (disposed || generation !== revision || !permitted(currentPermission()) || before.itemCount === 0) return clearOwn();
    let matching;
    try { matching = readGenuineMatchingCookies(env.document.cookie); }
    catch { return clearOwn(); }
    const captureAt = now();
    const envelope = buildStorefrontContext({ now: captureAt, sourceOrigin: env.location.origin, userAgent: env.navigator.userAgent, matching, privacy: currentPermission(), cartBinding: before.binding });
    if (!envelope || disposed || generation !== revision) return clearOwn();
    const signature = JSON.stringify([envelope.sourceUrl, envelope.userAgent, envelope.matching, envelope.cartBinding]);
    if (signature === lastSignature && captureAt - lastWrittenAt >= 0 && captureAt - lastWrittenAt < 5 * 60 * 1000) return { status: 'unchanged' };
    if (!permitted(currentPermission())) return clearOwn();
    await writeOwn(JSON.stringify(envelope));
    if (disposed || generation !== revision || !permitted(currentPermission())) return clearOwn();
    let after;
    try { after = await cart(); }
    catch { return clearOwn(); }
    if (disposed || generation !== revision || !permitted(currentPermission()) || after.binding !== before.binding || after.itemCount === 0) return clearOwn();
    lastSignature = signature;
    lastWrittenAt = captureAt;
    return { status: 'context_write_acknowledged' };
  }

  function refresh() {
    serial = serial.then(async () => {
      if (disposed) return { status: 'disposed' };
      const abort = new env.AbortController();
      const timer = env.setTimeout(() => abort.abort(), 3000);
      try {
        return await env.navigator.locks.request(CONTEXT_WRITE_LOCK, { mode: 'exclusive', signal: abort.signal }, async lock => {
          env.clearTimeout(timer);
          if (!lock || lock.name !== CONTEXT_WRITE_LOCK || lock.mode !== 'exclusive') return { status: 'held' };
          return work();
        });
      } finally { env.clearTimeout(timer); }
    }).catch(() => ({ status: 'held' }));
    return serial;
  }

  async function loadPrivacy() {
    if (env.Shopify?.customerPrivacy) return;
    if (typeof env.Shopify?.loadFeatures !== 'function') return;
    await new Promise(resolve => {
      let done = false;
      const finish = () => { if (!done) { done = true; env.clearTimeout(timer); resolve(); } };
      const timer = env.setTimeout(finish, 3000);
      try { env.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finish); }
      catch { finish(); }
    });
  }

  function listen(target, type, callback) {
    target.addEventListener(type, callback);
    listeners.push(() => target.removeEventListener(type, callback));
  }

  return {
    status: 'prepared_storefront_context',
    start() {
      if (started) return started;
      started = (async () => {
        listen(env.document, 'visitorConsentCollected', event => {
          revision += 1;
          const detail = event?.detail;
          if (detail?.marketingAllowed === false || detail?.saleOfDataAllowed === false) eventDenied = true;
          else if (detail?.marketingAllowed === true && detail?.saleOfDataAllowed === true) eventDenied = false;
          void refresh();
        });
        await loadPrivacy();
        if (disposed) return { status: 'disposed' };
        if (typeof config.subscribeCart === 'function') unsubscribe = config.subscribeCart(() => { void refresh(); });
        listen(env.document, 'visibilitychange', () => { if (env.document.visibilityState === 'visible') void refresh(); });
        if (typeof env.addEventListener === 'function') listen(env, 'pageshow', () => { void refresh(); });
        if (typeof env.setInterval === 'function') interval = env.setInterval(() => { if (env.document.visibilityState === 'visible') void refresh(); }, 5 * 60 * 1000);
        return refresh();
      })().catch(() => ({ status: 'held' }));
      return started;
    },
    refresh,
    dispose() {
      disposed = true;
      revision += 1;
      for (const remove of listeners) remove();
      if (typeof unsubscribe === 'function') unsubscribe();
      if (interval !== undefined) env.clearInterval(interval);
    }
  };
}

if (typeof window === 'undefined' || window.IPSConfirmedMetaContext) return;
const subscribeCart = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' ? callback => subscribe(PUB_SUB_EVENTS.cartUpdate, callback) : undefined;
const runtime = createStorefrontContextRuntime(window, {enabled:true,policyReviewed:true,approvedStorefrontCapture:true,subscribeCart});
window.IPSConfirmedMetaContext = runtime;
void runtime.start().catch(() => {});
})();
